import {
  Card,
  Text,
  IndexTable,
  Badge,
  Tabs,
  BlockStack,
  Icon,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { InvoiceData, InvoiceStatus } from "../types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en";
import { User } from "@shared/types/user";
import { BillIcon } from "@medentic/polaris-icons";

const getInvoiceStatusTranslationKey = (status: InvoiceStatus): string => {
  const statusKeyMap: Record<InvoiceStatus, string> = {
    [InvoiceStatus.PAID]: "settings.subscription.invoiceStatus.paid",
    [InvoiceStatus.FAILED]: "settings.subscription.invoiceStatus.failed",
  };
  return statusKeyMap[status] || `settings.subscription.invoiceStatus.${status}`;
};

dayjs.extend(utc);
dayjs.extend(timezone);

interface InvoiceListCardProps {
  clinicId: string;
}

export function InvoiceListCard({ clinicId }: InvoiceListCardProps) {
  const { t, i18n } = useTranslation();
  const { GET } = useApiRequest();

  const locale = i18n.language;
  const queryClient = useQueryClient();

  const user = queryClient.getQueryData<User>(["user"]);
  const userTimezone = user?.timezone?.id ?? "UTC";

  dayjs.locale(locale);

  const [selectedTab, setSelectedTab] = useState(0);

  const { data: invoices = [] } = useQuery<InvoiceData[]>({
    queryKey: ["invoices", clinicId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${clinicId}/billing/invoices`),
    enabled: !!clinicId,
  });

  const tabs = [
    {
      id: "all",
      content: t("settings.subscription.invoiceTabs.all"),
      accessibilityLabel: t("settings.subscription.invoiceTabs.all"),
      panelID: "all-invoices",
    },
    {
      id: "paid",
      content: t("settings.subscription.invoiceTabs.paid"),
      accessibilityLabel: t("settings.subscription.invoiceTabs.paid"),
      panelID: "paid-invoices",
    },
    {
      id: "failed",
      content: t("settings.subscription.invoiceTabs.failed"),
      accessibilityLabel: t("settings.subscription.invoiceTabs.failed"),
      panelID: "failed-invoices",
    },
  ];

  const handleTabChange = useCallback((selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  }, []);

  const filteredInvoices = invoices.filter((invoice) => {
    if (selectedTab === 1) return invoice.status === "paid";
    if (selectedTab === 2) return invoice.status === "failed";
    return true;
  });

  const resourceName = {
    singular: t("settings.subscription.invoiceTable.invoice"),
    plural: t("settings.subscription.invoiceTable.invoices"),
  };

  const rowMarkup = filteredInvoices.map((invoice, index) => {
    const formattedDate = dayjs
      .utc(invoice.dateIssued)
      .tz(userTimezone)
      .format("LL");

    return (
      <IndexTable.Row id={invoice.number} key={invoice.number} position={index}>
        <IndexTable.Cell>{formattedDate}</IndexTable.Cell>
        <IndexTable.Cell>{invoice.number}</IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={invoice.status === "paid" ? "success" : "critical"}>
            {t(getInvoiceStatusTranslationKey(invoice.status))}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span" variant="bodyMd" alignment="end">
            {invoice.amountLabel}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <a
            href={invoice.downloadLink}
            download={`invoice-${invoice.number}.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon source={BillIcon} tone="subdued" />
          </a>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Card>
      <Text variant="headingMd" as="h2">
        {t("settings.subscription.invoiceHistory")}
      </Text>
      <br />
      <Card padding="0">
        <BlockStack gap="100">
          <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
          <IndexTable
            selectable={false}
            resourceName={resourceName}
            itemCount={filteredInvoices.length}
            headings={[
              { title: t("settings.subscription.invoiceTable.dateIssued") },
              { title: t("settings.subscription.invoiceTable.number") },
              { title: t("settings.subscription.invoiceTable.status") },
              {
                title: t("settings.subscription.invoiceTable.amount"),
                alignment: "end",
              },
              { title: "" },
            ]}
            emptyState={
              <div style={{ padding: "20px" }}>
                {t("settings.subscription.invoiceTable.noInvoices")}
              </div>
            }
          >
            {rowMarkup}
          </IndexTable>
        </BlockStack>
      </Card>
    </Card>
  );
}
